import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, inject, SimpleChanges, OnChanges, OnDestroy, PLATFORM_ID } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

import { ToastService } from '../../services/toast.service';
import { CartService } from '../../services/cart.service';
import { CartPlan, CartProduct, CartProductSuggestion } from '../../../core/models/cart.model';
import { Icon } from '../../../core/models/icon.model';
import { DomainPromoComponent } from '../domain-promo/domain-promo.component';
import { CartProductSuggestionComponent } from '../cart-product-suggestion/cart-product-suggestion.component';
import { DEFAULT_DOMAIN_REGISTRATION_ICON } from '../../constants/domain-promo.constants';
import { IconComponent } from '../icon/icon.component';
import { CurrencyBRLPipe } from '../../pipes/currency-brl.pipe';
import { fadeCollapseAnimation } from '../../animations/fade-collapse.animations';
import { LoadingComponent } from '../loading/loading.component';
import { sanitizeSlug, TOP_NAV_HUB_SOLUCOES } from '../../../core/utils/utils';
import { NavigationFacade } from '../../../core/store/navigation/navigation.facade';
import { HeaderService } from '../../services/header.service';
import { I18nPipe } from '../../pipes/i18n.pipe';
import { I18nService } from '../../../core/services/i18n.service';
import { UtmPreservationService } from '../../../core/services/utm-preservation.service';
import { isOneTimeRegistryDomainPrice } from '../../../core/utils/cart-domain-suggestion-rules';

@Component({
  selector: 'umkt-cart-product-item',
  standalone: true,
  imports: [
    CommonModule,
    IconComponent,
    CurrencyBRLPipe,
    LoadingComponent,
    I18nPipe,
    DomainPromoComponent,
    CartProductSuggestionComponent,
  ],
  templateUrl: './cart-product-item.component.html',
  styleUrl: './cart-product-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeCollapseAnimation],
})
export class CartProductItemComponent implements OnChanges, OnDestroy {
  private toast = inject(ToastService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private navigationFacade = inject(NavigationFacade);
  private cartService = inject(CartService);
  private headerService = inject(HeaderService);
  private i18nService = inject(I18nService);
  private utmPreservationService = inject(UtmPreservationService);
  private platformId = inject(PLATFORM_ID);


  @Input({ required: true }) product!: CartProduct;
  @Input() showSubtotal: boolean = true;
  @Input() showPlanPrice: boolean = true;
  @Input() showRemoveAction: boolean = true;
  @Input() showQuantityControl: boolean = false;
  @Input() compact: boolean = false;
  @Input() currency: string = 'BRL';
  @Input() periodicitySuffix = { monthly: this.i18nService.instant('components.orderContent.perMonth'), yearly: this.i18nService.instant('components.orderContent.perYear'), once: '' };
  @Input() showDetailsLabel = false;
  @Input() lastChild = false;
  @Input() isChangePlan = false;
  @Input() changePlanLicenseCap?: number;
  @Input() maxQuantityByPlanId: Record<string, number> = {};

  @Output() removeProduct = new EventEmitter<string>();
  @Output() removePlan = new EventEmitter<{ planId: string }>();
  @Output() removeAddon = new EventEmitter<{
    planId: string;
    addonId: string;
  }>();
  @Output() undoRemove = new EventEmitter<{ productId: string; planId: string }>();
  @Output() changeQty = new EventEmitter<{
    planId: string;
    quantity: number;
  }>();
  @Output() openDetails = new EventEmitter<{ planId: string }>();
  @Output() suggestionAccept = new EventEmitter<CartProductSuggestion>();
  @Output() suggestionDismiss = new EventEmitter<CartProductSuggestion>();

  @Input() isCartEmpty: boolean = false;
  @Input() suggestionLoadingId: string | null = null;
  @Input() useCategoryLayoutWhenEmpty: boolean = false;
  @Input() showProductTerm: boolean = false; 

  loadingShow: boolean = false;
  removedPlans = new Set<string>();
  private destroyed = false;

  readonly fallbackDomainPromoIcon: Icon = DEFAULT_DOMAIN_REGISTRATION_ICON;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['product']) {
      this.removedPlans.clear();
      this.pending.clear();
      this.clearAllTimeouts();
      this.clearAllToasts();
      this.loadingShow = false;
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.pending.forEach((pendingItem, key) => {
      if (!pendingItem.undone) {
        if (pendingItem.type === 'plan') {
          const planId = key.replace('plan:', '');
          this.removePlan.emit({ planId });
        }
      }
    });
    
    this.clearAllToasts();
    this.clearAllTimeouts();
    this.pending.clear();
    this.removedPlans.clear();
  }

  private pending: Map<string, { type: 'plan' | 'product'; undone: boolean }> =
    new Map<string, { type: 'plan' | 'product'; undone: boolean }>();
  
  private timeouts: Map<string, ReturnType<typeof setTimeout>> = 
    new Map<string, ReturnType<typeof setTimeout>>();
  
  private toastIds: Map<string, number> = 
    new Map<string, number>();

  private clearAllTimeouts(): void {
    this.timeouts.forEach(timeout => clearTimeout(timeout));
    this.timeouts.clear();
  }

  private clearAllToasts(): void {
    this.toastIds.forEach(toastId => {
      this.toast.remove(toastId);
    });
    this.toastIds.clear();
  }

  public trackByPlanId(index: number, plan: CartPlan): string {
    return plan.id;
  }

  trackDomainPromoIndex(index: number): number {
    return index;
  }

  trackSuggestionIndex(index: number): number {
    return index;
  }

  private productIcons: Record<string, Icon> = {
    office: { name: 'cafe', type: 'mondrian-icons' },                 
    security: { name: 'escudo-pessoas', type: 'mondrian-icons' },        
    digitalPresence: { name: 'celular', type: 'mondrian-icons' },       
    business: { name: 'mala-mao', type: 'mondrian-icons' },              
    digitalPresenceAlt: { name: 'computador-celular', type: 'mondrian-icons' }, 
    cloud: { name: 'nuvem', type: 'mondrian-icons' },

  };


  getIcon(): Icon {
    const category = this.product.categories?.[0] || 'office';
    const icon = this.productIcons[category];   
    if (!icon) {
      return this.productIcons['office']; 
    }
    
    return icon;
  }

  onRemovePlan(planId: string): void {
    if (!planId || this.removedPlans.has(planId) || this.isChangePlan) return;

    const key = `plan:${planId}`;
    this.removedPlans.add(planId);
    this.pending.set(key, { type: 'plan', undone: false });
    this.loadingShow = true;

    const duration = 3000;
    const message = this.i18nService.instant('components.cartProductItem.removeSuccess');
    const toastId = this.toast.showSuccess(
      message,
      duration,
      'top-center',
      () => {
        if (this.destroyed) {
          return;
        }
        
        const p = this.pending.get(key);
        if (!p) return;
        p.undone = true;
        this.removedPlans.delete(planId);
        this.undoRemove.emit();
        this.pending.delete(key);
        
        const timeout = this.timeouts.get(key);
        if (timeout) {
          clearTimeout(timeout);
          this.timeouts.delete(key);
        }
        
        this.toastIds.delete(key);
      
        this.loadingShow = false;
      }
    );
    
    this.toastIds.set(key, toastId);

    const timeoutId = setTimeout(() => {
      const p = this.pending.get(key);
      this.pending.delete(key);
      this.timeouts.delete(key);
      this.toastIds.delete(key);
      if (!p) return;
      if (!p.undone) {
        this.removePlan.emit({ planId });
        this.loadingShow = false;
      }
    }, duration + 100);
    
    this.timeouts.set(key, timeoutId);
  }

  onDecrement(plan: CartPlan): void {
    const current = plan.quantity ?? 1;

    if (current > 1 && !this.isMind) {
      this.changeQty.emit({ planId: plan.id, quantity: current - 1 });
      return;
    }
    this.onRemovePlan(plan.id);
  }

  onIncrement(plan: CartPlan): void {
    if (this.isMind) {
      return;
    }
    const current = plan.quantity ?? 1;
    const maxQuantity = this.getPlanMaxQuantity(plan);
    if (maxQuantity != null && current >= maxQuantity) {
      return;
    }
    if (this.isChangePlan) {
      const cap = this.changePlanLicenseCap;
      if (cap == null || current >= cap) {
        return;
      }
      this.changeQty.emit({ planId: plan.id, quantity: current + 1 });
      return;
    }
    this.changeQty.emit({ planId: plan.id, quantity: current + 1 });
  }

  isPlusDisabled(plan: CartPlan): boolean {
    if (this.isMind) {
      return true;
    }
    const maxQuantity = this.getPlanMaxQuantity(plan);
    if (maxQuantity != null && (plan.quantity ?? 1) >= maxQuantity) {
      return true;
    }
    if (this.isChangePlan && this.changePlanLicenseCap != null) {
      return (plan.quantity ?? 1) >= this.changePlanLicenseCap;
    }
    return false;
  }

  private getPlanMaxQuantity(plan: CartPlan): number | null {
    const limit = this.maxQuantityByPlanId?.[plan.id];
    return limit != null && limit >= 0 ? limit : null;
  }

  public handleItemAction(): void {
    console.log('ação disparada');
  }

  onAdd(category: any) {
    const categorySlug = sanitizeSlug(category.name || '');
    this.navigationFacade.setCurrentCategory(categorySlug);
    
    const currentQueryParams: Record<string, string> = {};
    if (isPlatformBrowser(this.platformId)) {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.forEach((value, key) => {
        currentQueryParams[key] = value;
      });
    } else {
      Object.assign(currentQueryParams, this.route.snapshot.queryParams);
    }
    
    const navigationOptions: any = {};
    if (Object.keys(currentQueryParams).length > 0) {
      const queryParams = this.utmPreservationService.addPreservedUtmToQueryParams({ ...currentQueryParams });
      navigationOptions.queryParams = queryParams;
    }
    
    this.router.navigate([categorySlug], navigationOptions);
    this.cartService.closeCartSidebar();

    this.headerService.setActiveTopNavItem(TOP_NAV_HUB_SOLUCOES);
    
    this.headerService.triggerScrollTo(category.name);
  }

  isOneTimePlan(plan: CartPlan): boolean {
    return isOneTimeRegistryDomainPrice(this.product?.id ?? this.product?.uuid, plan.id);
  }

  showsMonthlySuffix(plan: CartPlan): boolean {
    return !this.isOneTimePlan(plan);
  }

  getAdjustedPrice(plan: CartPlan): number {
    if (this.isOneTimePlan(plan)) {
      return plan.unitPrice;
    }
    if (plan.paymentRecurrence === 'annual') {
      return plan.unitPrice / 12;
    }
    return plan.unitPrice;
  }

  formatPlanOriginalPrice(plan: CartPlan): string {
    return (plan.originalUnitPrice ?? 0).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  get isMind(): boolean {
    return (this.product as any)?.productType === 'MIND';
  }
}
